import { SUBMIT_PERSONS_ARRAY } from '../form.components/persons.array/reducer.jsx';
import { Configuration, Field, Row, Section, Page, Group, FinishPage } from '../engine/config.description.components.jsx';
import { getPropsOrDefault } from '../../common/utils.jsx';
import cancerList from './cancer.list.jsx';
import countriesList from './countries.list.jsx';
import ancestriesList from './ancestries.list.jsx';
import _ from '../i18n.jsx';
import GENETIC_TESTS_LIST from './genetic.tests.list.jsx';
import {
    AJAX_AUTOCOMPLETE,
    AUTOCOMPLETE_CHECKBOX,
    AUTOCOMPLETE_CHECKBOX_WITH_AGE,
    DATE_OF_BIRTH,
    FAMILY_CANCER_HISTORY,
    PERSONS_ARRAY,
    RADIO_BUTTONS,
    YES_NO_BUTTONS,
    CHECKBOX,
    TEXT_INPUT,
    NUMBER_INPUT,
    WEIGHT,
    HEIGHT,
    USER_COMMENTS
} from '../form.components/field.type.names.jsx';
import { STYLE_DISPLAY_BLOCK } from '../questionnaire.utils.jsx';
/*eslint no-lone-blocks: 0 */
/*eslint id-length: 0 */
/*eslint dot-notation: 0 */

const YES = 'yes';
const NO = 'no';
const UNKNOWN = 'unknown';

/*------------------------------------------PERSONAL INFORMATION------------------------------------------------------*/
const PERSONAL_INFORMATION = new Page('personal-information', { title: _('Personal Information') });
{
    const name = new Row('proband-name', { title: _('What is your name?') });
    name.add(new Field('name', TEXT_INPUT, { placeholder: _('Name') }));

    const gender = new Row('proband-gender', { title: _('Gender') });
    gender.add(new Field('gender', RADIO_BUTTONS, {
        id: 'gender-field',
        options: [{ title: _('Male'), value: 'MALE' }, { title: _('Female'), value: 'FEMALE' }]
    }));

    const dob = new Row('proband-dob', { title: _('Date of birth') });
    dob.add(new Field('dob', DATE_OF_BIRTH));

    const height = new Row('proband-height', { title: _('Height') });
    height.add(new Field('height', HEIGHT, { min: 300, max: 2400 }));

    const weight = new Row('proband-weight', { title: _('Weight') });
    weight.add(new Field('weight', WEIGHT, { min: 8000, max: 253000 }));

    const race = new Row('proband-race', { title: _('What racial group best describes you?') });
    race.add(new Field('race', CHECKBOX, { unknownAccepted: true, id: 'race', multipleChoice: true, options: [
        { title: _('Asian'), value: 1 },
        { title: _('Black or African American'), value: 2 },
        { title: _('Native American or Alaska Native'), value: 3 },
        { title: _('Native Hawaiian or Other Pacific Islander'), value: 4 },
        { title: _('White'), value: 5 }
    ] }));

    const ancestry = new Row('proband-ancestry', { title: `${_('What ancestry best describes you?')}\n${_('(you can choose more than one)')}` });
    ancestry.add(new Field('ancestry', AUTOCOMPLETE_CHECKBOX, {
        placeholder: _('Please describe'),
        variants: ancestriesList,
        showAll: true,
        unknownAccepted: true,
        unique: true,
        id: 'proband-ancestry'
    }));

    const isJewish = new Row('proband-jewish', {
        title: _('Are you of Jewish descent?'),
        helpText: _('Jewish heritage can affect risk of developing certain conditions. Answering this question will help provide better risk assessment.')
    });
    isJewish.add(new Field('jewish', YES_NO_BUTTONS, { unknownAccepted: true, id: 'is-jewish' }));

    const jewishType = new Section('jewish-type', { condition: (fields) => YES === fields['jewish'] });
    jewishType.add(new Field('jewishType', CHECKBOX, {
        unknownAccepted: true,
        title: _('Please specify:'),
        id: 'jewish-type-list',
        multipleChoice: false,
        options: [
            { title: _('Ashkenazi'),    value: 1 },
            { title: _('Sephardic'),    value: 2 },
            { title: _('Mizrahi'),      value: 3 },
            { title: _('Other Jewish'), value: 4 }
        ]
    }));

    PERSONAL_INFORMATION.include([ name, gender, dob, height, weight, race, ancestry, isJewish, jewishType ]);
}
/*--------------------------------------------------------------------------------------------------------------------*/

/*---------------------------------------QUESTIONNAIRE LINK-----------------------------------------------------------*/
const WELCOME_PAGE = new Page('welcome-page', { title: _('Welcome') });
{
    const welcomePage = new Row('welcome-page');
    WELCOME_PAGE.include([welcomePage]);
}
/*--------------------------------------------------------------------------------------------------------------------*/

/*-------------------------------------HEALTH INFORMATION GROUP-------------------------------------------------------*/
const GROUP_HEALTH_INFORMATION = new Group('health-information', _('Health Information'));
/*--------------------------------------------------------------------------------------------------------------------*/

/*------------------------------------------REPRODUCTIVE HISTORY------------------------------------------------------*/
const REPRODUCTIVE_HISTORY = new Page('reproductive-history', { title: _('Reproductive History'), condition: (fields) => fields.gender && ('female' ===  fields.gender.toLowerCase()) });
{
    const firstMenstrual = new Row('first-menstrual-period', { title: _('At what age did you have your first menstrual period?') });
    firstMenstrual.add(new Field('first_menstrual', NUMBER_INPUT, { unknownAccepted: true }));

    const pregnancyCount = new Row('pregnancy-count', {
        title: _('How many times (if any) have you been pregnant? (This includes full-term, partial birth, stillborn, and terminated pregnancies)'),
        helpText: _('The hormonal changes that occur during pregnancy may influence a woman’s chance of developing certain cancers later in life.')
    });
    pregnancyCount.add(new Field('pregnancy_count', NUMBER_INPUT, { unknownAccepted: true }));

    const pregnancyInfo = new Row('pregnancy-info', { title: _('How many pregnancies have come to term (live birth)?'), condition: (fields) => {
        const pregnancyCountValue = parseInt(fields['pregnancy_count']);
        return !isNaN(pregnancyCountValue) && 0 < pregnancyCountValue;
    } });
    pregnancyInfo.add(new Field('pregnancyTerminatedCount', NUMBER_INPUT, { unknownAccepted: true }));

    const liveBirth = new Section('first-live-birth');
    liveBirth.add(new Field('first-live-birth', NUMBER_INPUT, {
        title: _('At what age was your first live birth?'),
        label: _('years'),
        unknownAccepted: true
    }));

    const breastFeed = new Section('breast-feed');
    breastFeed.add(new Field('breast-feed', YES_NO_BUTTONS, {
        id: 'breast-feed',
        title: _('Did you breast feed?'),
        unknownAccepted: true
    }));

    const breastFeedLength = new Section('breast-feed-length', { condition: (fields) => YES === fields['breast-feed'] });
    breastFeedLength.add(new Field('breast-feed-length', NUMBER_INPUT, {
        id: 'breast-feed-length',
        label: _('months'),
        title: _('For how long (total number of months for all children)?'),
        unknownAccepted: true
    }));

    pregnancyInfo.include([ liveBirth, breastFeed, breastFeedLength ]);

    REPRODUCTIVE_HISTORY.include([ firstMenstrual, pregnancyCount, pregnancyInfo ]);
}
/*--------------------------------------------------------------------------------------------------------------------*/

/*----------------------------------------CANCER HISTORY--------------------------------------------------------------*/
const CANCER_HISTORY = new Page('cancer-history', { title: _('Cancer History') });
{
    const cancerDiagnosed = new Row('cancer-diagnosed', { title: _('Have you ever been diagnosed with cancer?') });
    cancerDiagnosed.add(new Field('cancer_diagnosed', YES_NO_BUTTONS, { unknownAccepted: true, id: 'cancer-diagnosed' }));

    const cancerType = new Section('cancer-type', { condition: (fields) => YES === fields['cancer_diagnosed'] });
    cancerType.add(new Field('cancerList', AUTOCOMPLETE_CHECKBOX_WITH_AGE, {
        id: 'cancer-type',
        placeholder: _('Other, please describe'),
        showAll: true,
        variants: cancerList,
        title: _('Which type?'),
        defaultVariants: [ 'Invitae80', 'Invitae121', 'Invitae435', 'Invitae176', 'Invitae438' ]
    }));

    const cancerDiagnosedRelative = new Row('cancer-diagnosed-relative', { title: _('Have any of your family members been diagnosed with cancer?') });
    cancerDiagnosedRelative.add(new Field('cancer_diagnosed_relative', YES_NO_BUTTONS, { unknownAccepted: true, id: 'cancer-diagnosed-relative' }));

    const cancerTypeRelative = new Section('cancer-type', { condition: (fields) => YES === fields['cancer_diagnosed_relative'] });
    cancerTypeRelative.add(new Field('cancer_type_relative', FAMILY_CANCER_HISTORY, {
        title: _('Indicate the relationship of the person to you, cancer type, and age of onset')
    }));

    CANCER_HISTORY.include([ cancerDiagnosed, cancerType, cancerDiagnosedRelative, cancerTypeRelative ]);
}
/*--------------------------------------------------------------------------------------------------------------------*/

/*----------------------------------------GENETIC TESTING-------------------------------------------------------------*/
const GENETIC_TESTING = new Page('genetic-testing', { title: _('Genetic Testing') });
{
    const geneticTesting = new Row('genetic-testing', { title: _('Have you ever had genetic testing?') });
    geneticTesting.add(new Field('genetic_testing', YES_NO_BUTTONS, { unknownAccepted: true, id: 'genetic-testing' }));

    const geneticTestingList = new Section('genetic-testing-list', { condition: (fields) => YES === fields['genetic_testing'] });
    geneticTestingList.add(new Field('genetic_testing_list', AUTOCOMPLETE_CHECKBOX, {
        id: 'genetic-testing-list',
        title: _('For which genes?'),
        multipleChoice: true,
        unique: true,
        variants: GENETIC_TESTS_LIST,
        defaultVariants: [ 1, 2, 3, 4, 5, 6 ],
        placeholder: _('Other, please describe')
    }));

    GENETIC_TESTING.include([ geneticTesting, geneticTestingList ]);
}
/*--------------------------------------------------------------------------------------------------------------------*/

/*----------------------------------------SURGICAL HISTORY------------------------------------------------------------*/
const SURGICAL_HISTORY = new Page('surgical-history', { title: _('Surgical History') });
{
    const maleSurgicalHistory = new Row('surgical-history-for-male', {
        title: _('Have you ever had a mastectomy?'),
        condition: (fields) => fields.gender && ('male' === fields.gender.toLowerCase())
    });
    maleSurgicalHistory.add(new Field('surgical_history_for_male', YES_NO_BUTTONS, { unknownAccepted: true, id: 'surgical-history-for-male' }));

    const femaleSurgicalHistory = new Row('surgical-history-for-female', {
        title: _('Have you ever had a mastectomy or oophorectomy?'),
        condition: (fields) => (fields.gender && 'male' !== fields.gender.toLowerCase()) || !fields.gender
    });
    femaleSurgicalHistory.add(new Field('surgical_history_for_female', YES_NO_BUTTONS, { unknownAccepted: true, id: 'surgical-history-for-female' }));

    const surgicalHistoryList = new Section('surgical-history-list', {
        condition: (fields) => (YES === fields['surgical_history_for_female']) && 'undefined' !== typeof fields.gender && ('male' !== fields.gender.toLowerCase())
    });
    surgicalHistoryList.add(new Field('surgical_history_list', AUTOCOMPLETE_CHECKBOX, {
        id: 'surgical-history-list',
        title    : _('For which:'),
        multipleChoice: true,
        unique: true,
        placeholder: _('Other, please describe'),
        variants: [
            { name: _('Mastectomy'), value: 1 },
            { name: _('Oophorectomy'), value: 2 }
        ],
        defaultVariants: [ 1, 2 ]
    }));

    const breastBiopsy = new Row('breast-biopsy', {
        title: _('Have you ever had a breast biopsy?'),
        condition: (fields) => fields.gender && 'female' === fields.gender.toLowerCase()
    });
    breastBiopsy.add(new Field('breast_biopsy', YES_NO_BUTTONS, { unknownAccepted: true, id: 'breast-biopsy' }));

    const breastBiopsyDetails = new Section('breast-biopsy-details', { condition: (fields) => YES === fields['breast_biopsy'] });
    breastBiopsyDetails.add(new Field('breast-biopsy-count', RADIO_BUTTONS, {
        id: 'breast-biopsy-count',
        title : _('How many biopsies have you had?'),
        options: [{ title: _('1'), value: '1' }, { title: _('> 1'), value: '> 1' }]
    }));
    breastBiopsyDetails.add(new Field('atypical_hyperplasia', YES_NO_BUTTONS, {
        id: 'atypical-hyperplasia',
        title: _('Have you had a breast biopsy with atypical hyperplasia?'),
        unknownAccepted: true
    }));


    SURGICAL_HISTORY.include([ maleSurgicalHistory, femaleSurgicalHistory, surgicalHistoryList, breastBiopsy, breastBiopsyDetails ]);
}
/*--------------------------------------------------------------------------------------------------------------------*/

/*------------------------------------------FAMILY TREE---------------------------------------------------------------*/
const FAMILY_TREE = new Group('family-tree', _('Family Tree'));
/*--------------------------------------------------------------------------------------------------------------------*/

/*--------------------------------------------MOTHER------------------------------------------------------------------*/
const MOTHER = new Page('mother', { title: _('Mother') });
{
    const name = new Row('mother-name', { title: _('Name') });
    name.add(new Field('name', TEXT_INPUT, { placeholder: `${_('Example:')} ${_('Mary')}`, root: 'mother' }));

    const isAlive = new Row('mother-alive', { title: _('Is she alive?') });
    isAlive.add(new Field('alive', YES_NO_BUTTONS, { unknownAccepted: true, id: 'mother-alive', root: 'mother' }));

    const birthAndDeathInfo = new Section('mother-birth-and-death', {
        condition: (fields) => UNKNOWN !== getPropsOrDefault(fields, 'mother.alive', UNKNOWN).toLowerCase()
    });
    birthAndDeathInfo.add(new Field('dob', DATE_OF_BIRTH, {
        title: _('Date of birth'),
        root: 'mother',
        condition: (fields) => YES === getPropsOrDefault(fields, 'mother.alive', UNKNOWN)
    }));
    birthAndDeathInfo.add(new Field('diedAt', NUMBER_INPUT, {
        title: _('At what age did she die?'),
        length: 3,
        root: 'mother',
        unknownAccepted: true,
        label: _('years'),
        condition: (fields) => NO === getPropsOrDefault(fields, 'mother.alive', UNKNOWN)
    }));
    birthAndDeathInfo.add(new Field('causeOfDeath', AJAX_AUTOCOMPLETE, {
        id: 'mother-causeOfDeath',
        placeholder: `${_('Example:')} ${_('Cancer')}`,
        title: _('Cause of death'),
        prefetch: true,
        service: 'concept',
        showAll: true,
        conceptType: 'conditions',
        storageName: 'conditions',
        root: 'mother',
        componentName: 'mother-causeOfDeath',
        wrapperStyle: STYLE_DISPLAY_BLOCK,
        condition: (fields) => NO === getPropsOrDefault(fields, 'mother.alive', UNKNOWN)
    }));

    const ancestorComingFrom = new Row('mother-ancestors', {
        title: `${_('What country were your mother\'s ancestors (your grand/great-grandparents) originally from before moving to the U.S.?')} ${_('Please enter all that apply.')}`
    });
    ancestorComingFrom.add(new Field('ancestors', AJAX_AUTOCOMPLETE, {
        id: 'mother-ancestors',
        variants: countriesList,
        componentName: 'mother-ancestors',
        placeholder: `${_('Example:')} ${_('Italy')}`,
        root: 'mother',
        showAll: true
    }));

    const ancestry = new Row('mother-ancestry', { title: `${_('What ancestry best describes your mother?')} ${_('(you can choose more than one)')}` });
    ancestry.add(new Field('ancestry', AUTOCOMPLETE_CHECKBOX, {
        id: 'mother-ancestry',
        unknownAccepted: true,
        placeholder: _('Please describe'),
        variants: ancestriesList,
        showAll: true,
        root: 'mother',
        unique: true
    }));

    const geneticTesting = new Row('mother-genetic-testing', { title: _('Has she ever had genetic testing?') });
    geneticTesting.add(new Field('genetic_testing', YES_NO_BUTTONS, { unknownAccepted: true, id: 'genetic-testing', root: 'mother' }));

    const geneticTestingList = new Section('genetic-testing-list', {
        condition: (fields) => YES === getPropsOrDefault(fields, 'mother.genetic_testing', NO)
    });
    geneticTestingList.add(new Field('genetic_testing_list', AUTOCOMPLETE_CHECKBOX, {
        id: 'mother-genetic-testing-list',
        title: _('For which genes?'),
        multipleChoice: true,
        unique: true,
        variants: GENETIC_TESTS_LIST,
        defaultVariants: [ 1, 2, 3, 4, 5, 6 ],
        placeholder: _('Other, please describe'),
        root: 'mother'
    }));

    MOTHER.include([ name, isAlive, birthAndDeathInfo, ancestorComingFrom, ancestry, geneticTesting, geneticTestingList ]);
}
/*--------------------------------------------------------------------------------------------------------------------*/

/*--------------------------------------------FATHER------------------------------------------------------------------*/
const FATHER = new Page('father', { title: _('Father') });
{
    const name = new Row('father-name', { title: _('Name') });
    name.add(new Field('name', TEXT_INPUT, { placeholder: `${_('Example:')} ${_('Bob')}`, root: 'father' }));

    const isAlive = new Row('father-alive', { title: _('Is he alive?') });
    isAlive.add(new Field('alive', YES_NO_BUTTONS, { unknownAccepted: true, id: 'father-alive', root: 'father' }));

    const birthAndDeathInfo = new Section('father-birth-and-death', {
        condition: (fields) => UNKNOWN !== getPropsOrDefault(fields, 'father.alive', UNKNOWN).toLowerCase()
    });
    birthAndDeathInfo.add(new Field('dob', DATE_OF_BIRTH, {
        title: _('Date of birth'),
        root: 'father',
        condition: (fields) => YES === getPropsOrDefault(fields, 'father.alive', UNKNOWN)
    }));
    birthAndDeathInfo.add(new Field('diedAt', NUMBER_INPUT, {
        title: _('At what age did he die?'),
        length: 3,
        root: 'father',
        unknownAccepted: true,
        label: _('years'),
        condition: (fields) => NO === getPropsOrDefault(fields, 'father.alive', UNKNOWN)
    }));
    birthAndDeathInfo.add(new Field('causeOfDeath', AJAX_AUTOCOMPLETE, {
        id: 'father-causeOfDeath',
        placeholder: `${_('Example:')} ${_('Cancer')}`,
        title: _('Cause of death'),
        service: 'concept',
        conceptType: 'conditions',
        storageName: 'conditions',
        showAll: true,
        prefetch: true,
        root: 'father',
        componentName: 'father-causeOfDeath',
        wrapperStyle: STYLE_DISPLAY_BLOCK,
        condition: (fields) => NO === getPropsOrDefault(fields, 'father.alive', UNKNOWN)
    }));

    const ancestorComingFrom = new Row('father-ancestors', {
        title: `${_('What country were your father\'s ancestors (your grand/great-grandparents) originally from before moving to the U.S.?')} ${_('Please enter all that apply.')}`
    });
    ancestorComingFrom.add(new Field('ancestors', AJAX_AUTOCOMPLETE, {
        id: 'father-ancestors',
        variants: countriesList,
        placeholder: `${_('Example:')} ${_('Poland')}`,
        componentName: 'father-ancestors',
        showAll: true,
        root: 'father'
    }));

    const ancestry = new Row('father-ancestry', { title: `${_('What ancestry best describes your father?')} ${_('(you can choose more than one)')}` });
    ancestry.add(new Field('ancestry', AUTOCOMPLETE_CHECKBOX, {
        id: 'father-ancestry',
        unknownAccepted: true,
        placeholder: _('Please describe'),
        showAll: true,
        root: 'father',
        unique: true,
        variants: ancestriesList
    }));

    const geneticTesting = new Row('father-genetic-testing', { title: _('Has he ever had genetic testing?') });
    geneticTesting.add(new Field('genetic_testing', YES_NO_BUTTONS, { unknownAccepted: true, id: 'genetic-testing', root: 'father' }));

    const geneticTestingList = new Section('genetic-testing-list', { condition: (fields) => YES === getPropsOrDefault(fields, 'father.genetic_testing', NO) });
    geneticTestingList.add(new Field('genetic_testing_list', AUTOCOMPLETE_CHECKBOX, {
        id: 'father-genetic-testing-list',
        title: _('For which genes?'),
        multipleChoice: true,
        unique: true,
        variants: GENETIC_TESTS_LIST,
        defaultVariants: [ 1, 2, 3, 4, 5, 6 ],
        placeholder: _('Other, please describe'),
        root: 'father'
    }));

    FATHER.include([ name, isAlive, birthAndDeathInfo, ancestorComingFrom, ancestry, geneticTesting, geneticTestingList ]);
}
/*------------------------------------------------CHILDREN------------------------------------------------------------*/
const CHILDREN = new Page('children', {
    title: _('Children'),
    actionObject: { type: SUBMIT_PERSONS_ARRAY, name: 'children' },
    transition: (fieldValues) => {
        const persons = fieldValues.children;
        const hasPersons = persons.data && 0 < Object.keys(persons.data).length;
        return persons.submit || !hasPersons;
    }
});
{
    const childrenRow = new Row('children', { disableNumber: true });
    childrenRow.add(new Field('children', PERSONS_ARRAY, {
        schemaName: 'CHILDREN',
        title: _('How many sons and/or daughters do you have?'),
        disableNumber: true
    }));

    CHILDREN.include([childrenRow]);
}
/*--------------------------------------------------------------------------------------------------------------------*/

/*------------------------------------------------SIBLINGS------------------------------------------------------------*/
const SIBLINGS = new Page('siblings', {
    title: _('Siblings (including half-siblings)'),
    actionObject: { type: SUBMIT_PERSONS_ARRAY, name: 'siblings' },
    transition: (fieldValues) => {
        const persons = fieldValues.siblings;
        const hasPersons = persons.data && 0 < Object.keys(persons.data).length;
        return persons.submit || !hasPersons;
    }
});
{
    const siblingsRow = new Row('siblings', { disableNumber: true });
    siblingsRow.add(new Field('siblings', PERSONS_ARRAY, {
        schemaName: 'SIBLING',
        title: _('How many sisters and brothers (full and half) do you have?')
    }));

    SIBLINGS.include([siblingsRow]);
}
/*--------------------------------------------------------------------------------------------------------------------*/

/*-------------------------------------------------NEPHEWS------------------------------------------------------------*/
const NEPHEWS = new Page('nephews', {
    title: `${_('Nephews')}/${_('Nieces')}`,
    actionObject: { type: SUBMIT_PERSONS_ARRAY, name: 'nephews' },
    transition: (fieldValues) => {
        const persons = fieldValues.nephews;
        const hasPersons = persons.data && 0 < Object.keys(persons.data).length;
        return persons.submit || !hasPersons;
    }
});
{
    const nephewsRow = new Row('nephews', { disableNumber: true });
    nephewsRow.add(new Field('nephews', PERSONS_ARRAY, {
        schemaName: 'NEPHEW',
        title: _('How many nephews and nieces do you have?')
    }));

    NEPHEWS.include([nephewsRow]);
}
/*--------------------------------------------------------------------------------------------------------------------*/

/*--------------------------------------------MOTHER'S SIBLINGS-------------------------------------------------------*/
const MOTHERS_SIBLINGS = new Page('motherSiblings', {
    title: _(`Mother's Siblings`),
    actionObject: { type: SUBMIT_PERSONS_ARRAY, name: 'mother_siblings' },
    transition: (fieldValues) => {
        const persons = fieldValues.mother_siblings;
        const hasPersons = persons.data && 0 < Object.keys(persons.data).length;
        return persons.submit || !hasPersons;
    }
});
{
    const motherSiblingsRow = new Row('motherSiblings', { disableNumber: true });
    motherSiblingsRow.add(new Field('mother_siblings', PERSONS_ARRAY, {
        schemaName: 'UNCLE_MATERNAL',
        title: _('How many sisters and brothers does your mother have?')
    }));

    MOTHERS_SIBLINGS.include([motherSiblingsRow]);
}
/*--------------------------------------------------------------------------------------------------------------------*/

/*--------------------------------------------FATHER'S SIBLINGS-------------------------------------------------------*/
const FATHERS_SIBLINGS = new Page('fatherSiblings', {
    title: _(`Father's Siblings`),
    actionObject: { type: SUBMIT_PERSONS_ARRAY, name: 'father_siblings' },
    transition: (fieldValues) => {
        const persons = fieldValues.father_siblings;
        const hasPersons = persons.data && 0 < Object.keys(persons.data).length;
        return persons.submit || !hasPersons;
    }
});
{
    const fatherSiblingsRow = new Row('fatherSiblings', { disableNumber: true });
    fatherSiblingsRow.add(new Field('father_siblings', PERSONS_ARRAY, {
        schemaName: 'UNCLE_PATERNAL',
        title: _('How many sisters and brothers does your father have?')
    }));

    FATHERS_SIBLINGS.include([fatherSiblingsRow]);
}
/*--------------------------------------------------------------------------------------------------------------------*/

/*-------------------------------------------------COUSINS------------------------------------------------------------*/
const COUSINS = new Page('cousins', {
    title: _(`Cousins`),
    actionObject: { type: SUBMIT_PERSONS_ARRAY, name: 'cousins' },
    transition: (fieldValues) => {
        const persons = fieldValues.cousins;
        const hasPersons = persons.data && 0 < Object.keys(persons.data).length;
        return persons.submit || !hasPersons;
    }
});
{
    const cousinsRow = new Row('cousins', { disableNumber: true });
    cousinsRow.add(new Field('cousins', PERSONS_ARRAY, {
        schemaName: 'COUSIN',
        title: _('How many cousins do you have?')
    }));

    COUSINS.include([cousinsRow]);
}
/*--------------------------------------------------------------------------------------------------------------------*/

/*--------------------------------------------MOTHER PARENTS----------------------------------------------------------*/
const MOTHERS_PARENTS = new Page('motherParents', { title: _(`Mother's Parents`) });
{
    const grandMother = new Row('maternal-grandmother', { title: _(`Mother's Mother`) });
    grandMother.add(new Field('name', TEXT_INPUT, { placeholder: _('Name'), title: _('Name'), root: 'maternal_grandmother' }));
    grandMother.add(new Field('alive', YES_NO_BUTTONS, { unknownAccepted: true, id: 'alive', title: _('Is she alive?'), root: 'maternal_grandmother' }));
    const motherDobOrDied = new Section('maternal-grandmother-birth-death-info', {
        condition: (fields) => UNKNOWN !== getPropsOrDefault(fields, 'maternal_grandmother.alive', UNKNOWN).toLowerCase()
    });
    motherDobOrDied.add(new Field('dob', DATE_OF_BIRTH, {
        title: _('Date of birth'),
        root: 'maternal_grandmother',
        condition: (fields) => YES === getPropsOrDefault(fields, 'maternal_grandmother.alive', UNKNOWN)
    }));
    motherDobOrDied.add(new Field('diedAt', NUMBER_INPUT, {
        title: _('At what age did she die?'),
        length: 3,
        label: _('years'),
        unknownAccepted: true,
        root: 'maternal_grandmother',
        condition: (fields) => NO === getPropsOrDefault(fields, 'maternal_grandmother.alive', UNKNOWN)
    }));
    motherDobOrDied.add(new Field('causeOfDeath', AJAX_AUTOCOMPLETE, {
        placeholder: `${_('Example:')} ${_('Cancer')}`,
        title: _('Cause of death'),
        unknownAccepted: true,
        service: 'concept',
        conceptType: 'conditions',
        storageName: 'conditions',
        id: 'maternal-grandmother-causeOfDeath',
        componentName: 'maternal-grandmother-causeOfDeath',
        wrapperStyle: STYLE_DISPLAY_BLOCK,
        root: 'maternal_grandmother',
        prefetch: true,
        showAll: true,
        condition: (fields) => NO === getPropsOrDefault(fields, 'maternal_grandmother.alive', UNKNOWN)
    }));

    const motherGeneticTests = new Row('maternal-grandmother-genetic-tests', { title: _('Has she ever had genetic testing?') });
    motherGeneticTests.add(new Field('genetic_testing', YES_NO_BUTTONS, { unknownAccepted: true, id: 'maternal-grandmother-gt', root: 'maternal_grandmother' }));
    const motherGeneticTestsListSection = new Section('maternal-grandmother-genetic-tests-list', {
        condition: (fields) => YES === getPropsOrDefault(fields, 'maternal_grandmother.genetic_testing', UNKNOWN)
    });
    motherGeneticTestsListSection.add(new Field('genetic_testing_list', AUTOCOMPLETE_CHECKBOX, {
        id: 'maternal-grandmother-genetic-testing-list',
        title: _('For which genes?'),
        multipleChoice: true,
        variants: GENETIC_TESTS_LIST,
        unique: true,
        defaultVariants: [ 1, 2, 3, 4, 5, 6 ],
        placeholder: _('Other, please describe'),
        root: 'maternal_grandmother'
    }));

    const grandFather = new Row('maternal-grandfather', { title: _(`Mother's Father`) });
    grandFather.add(new Field('name', TEXT_INPUT, { placeholder: _('Name'), title: _('Name'), root: 'maternal_grandfather' }));
    grandFather.add(new Field('alive', YES_NO_BUTTONS, { unknownAccepted: true, id: 'alive', title: _('Is he alive?'), root: 'maternal_grandfather' }));
    const fatherDobOrDied = new Section('maternal-grandfather-birth-death-info', {
        condition: (fields) => UNKNOWN !== getPropsOrDefault(fields, 'maternal_grandfather.alive', UNKNOWN).toLowerCase()
    });
    fatherDobOrDied.add(new Field('dob', DATE_OF_BIRTH, {
        title: _('Date of birth'),
        root: 'maternal_grandfather',
        condition: (fields) => YES === getPropsOrDefault(fields, 'maternal_grandfather.alive', UNKNOWN)
    }));
    fatherDobOrDied.add(new Field('diedAt', NUMBER_INPUT, {
        title: _('At what age did he die?'),
        length: 3,
        label: _('years'),
        unknownAccepted: true,
        root: 'maternal_grandfather',
        condition: (fields) => NO === getPropsOrDefault(fields, 'maternal_grandfather.alive', UNKNOWN)
    }));
    fatherDobOrDied.add(new Field('causeOfDeath', AJAX_AUTOCOMPLETE, {
        placeholder: `${_('Example:')} ${_('Cancer')}`,
        title: _('Cause of death'),
        unknownAccepted: true,
        service: 'concept',
        conceptType: 'conditions',
        storageName: 'conditions',
        componentName: 'maternal-grandfather-causeOfDeath',
        id: 'maternal-grandfather-causeOfDeath',
        wrapperStyle: STYLE_DISPLAY_BLOCK,
        root: 'maternal_grandfather',
        prefetch: true,
        showAll: true,
        condition: (fields) => NO === getPropsOrDefault(fields, 'maternal_grandfather.alive', UNKNOWN)
    }));

    const fatherGeneticTests = new Row('maternal-grandfather-genetic-tests', { title: _('Has he ever had genetic testing?') });
    fatherGeneticTests.add(new Field('genetic_testing', YES_NO_BUTTONS, { unknownAccepted: true, id: 'maternal-grandmother-gt', root: 'maternal_grandfather' }));
    const fatherGeneticTestsListSection = new Section('maternal-grandmother-genetic-tests-list', {
        condition: (fields) => YES === getPropsOrDefault(fields, 'maternal_grandfather.genetic_testing', UNKNOWN)
    });
    fatherGeneticTestsListSection.add(new Field('genetic_testing_list', AUTOCOMPLETE_CHECKBOX, {
        id: 'maternal-grandfather-genetic-testing-list',
        title: _('For which genes?'),
        multipleChoice: true,
        variants: GENETIC_TESTS_LIST,
        unique: true,
        defaultVariants: [ 1, 2, 3, 4, 5, 6 ],
        placeholder: _('Other, please describe'),
        root: 'maternal_grandfather'
    }));

    MOTHERS_PARENTS.include([
        grandMother, motherDobOrDied, motherGeneticTests, motherGeneticTestsListSection,
        grandFather, fatherDobOrDied, fatherGeneticTests, fatherGeneticTestsListSection
    ]);
}
/*--------------------------------------------------------------------------------------------------------------------*/

/*--------------------------------------------FATHER PARENTS----------------------------------------------------------*/
const FATHER_PARENTS = new Page('fatherParents', { title: _(`Father's Parents`) });
{
    const grandMother = new Row('paternal-grandmother', { title: _(`Father's Mother`) });
    grandMother.add(new Field('name', TEXT_INPUT, { placeholder: _('Name'), title: _('Name'), root: 'paternal_grandmother' }));
    grandMother.add(new Field('alive', YES_NO_BUTTONS, { unknownAccepted: true, id:'alive', title: _('Is she alive?'), root: 'paternal_grandmother' }));
    const motherDobOrDied = new Section('maternal-paternal-grandmother-birth-death-info', {
        condition: (fields) => UNKNOWN !== getPropsOrDefault(fields, 'paternal_grandmother.alive', UNKNOWN).toLowerCase()
    });
    motherDobOrDied.add(new Field('dob', DATE_OF_BIRTH, {
        title: _('Date of birth'),
        root: 'paternal_grandmother',
        condition: (fields) => YES === getPropsOrDefault(fields, 'paternal_grandmother.alive', UNKNOWN)
    }));
    motherDobOrDied.add(new Field('diedAt', NUMBER_INPUT, {
        title: _('At what age did she die?'),
        length: 3,
        label: _('years'),
        unknownAccepted: true,
        root: 'paternal_grandmother',
        condition: (fields) => NO === getPropsOrDefault(fields, 'paternal_grandmother.alive', UNKNOWN)
    }));
    motherDobOrDied.add(new Field('causeOfDeath', AJAX_AUTOCOMPLETE, {
        placeholder: `${_('Example:')} ${_('Cancer')}`,
        title: _('Cause of death'),
        unknownAccepted: true,
        service: 'concept',
        conceptType: 'conditions',
        storageName: 'conditions',
        componentName: 'paternal-grandmother-causeOfDeath',
        id: 'paternal-grandmother-causeOfDeath',
        wrapperStyle: STYLE_DISPLAY_BLOCK,
        root: 'paternal_grandmother',
        showAll: true,
        prefetch: true,
        condition: (fields) => NO === getPropsOrDefault(fields, 'paternal_grandmother.alive', UNKNOWN)
    }));

    const motherGeneticTests = new Row('paternal-grandmother-genetic-tests', { title: _('Has she ever had genetic testing?') });
    motherGeneticTests.add(new Field('genetic_testing', YES_NO_BUTTONS, { unknownAccepted: true, id: 'paternal-grandmother-gt', root: 'paternal_grandmother' }));
    const motherGeneticTestsListSection = new Section('maternal-grandmother-genetic-tests-list', {
        condition: (fields) => YES === getPropsOrDefault(fields, 'paternal_grandmother.genetic_testing', UNKNOWN)
    });
    motherGeneticTestsListSection.add(new Field('genetic_testing_list', AUTOCOMPLETE_CHECKBOX, {
        id: 'paternal-grandmother-genetic-testing-list',
        title: _('For which genes?'),
        multipleChoice: true,
        variants: GENETIC_TESTS_LIST,
        unique: true,
        defaultVariants: [ 1, 2, 3, 4, 5, 6 ],
        placeholder: _('Other, please describe'),
        root: 'paternal_grandmother'
    }));

    const grandFather = new Row('paternal-grandfather', { title: _(`Father's Father`) });
    grandFather.add(new Field('name', TEXT_INPUT, { placeholder: 'Name', title: _('Name'), root: 'paternal_grandfather' }));
    grandFather.add(new Field('alive', YES_NO_BUTTONS, { unknownAccepted: true, title: _('Is he alive?'), root: 'paternal_grandfather' }));
    const fatherDobOrDied = new Section('paternal-grandfather-birth-death-info', {
        condition: (fields) => UNKNOWN !== getPropsOrDefault(fields, 'paternal_grandfather.alive', UNKNOWN).toLowerCase()
    });
    fatherDobOrDied.add(new Field('dob', DATE_OF_BIRTH, {
        title: _('Date of birth'),
        root: 'paternal_grandfather',
        condition: (fields) => YES === getPropsOrDefault(fields, 'paternal_grandfather.alive', UNKNOWN)
    }));
    fatherDobOrDied.add(new Field('diedAt', NUMBER_INPUT, {
        title: _('At what age did he die?'),
        length: 3,
        label: _('years'),
        unknownAccepted: true,
        root: 'paternal_grandfather',
        condition: (fields) => NO === getPropsOrDefault(fields, 'paternal_grandfather.alive', UNKNOWN)
    }));
    fatherDobOrDied.add(new Field('causeOfDeath', AJAX_AUTOCOMPLETE, {
        placeholder: `${_('Example:')} ${_('Cancer')}`,
        title: _('Cause of death'),
        unknownAccepted: true,
        service: 'concept',
        prefetch: true,
        conceptType: 'conditions',
        storageName: 'conditions',
        componentName: 'paternal-grandfather-causeOfDeath',
        id: 'paternal-grandfather-causeOfDeath',
        wrapperStyle: STYLE_DISPLAY_BLOCK,
        showAll: true,
        root: 'paternal_grandfather',
        condition: (fields) => NO === getPropsOrDefault(fields, 'paternal_grandfather.alive', UNKNOWN)
    }));

    const fatherGeneticTests = new Row('paternal-grandfather-genetic-tests', { title: _('Has he ever had genetic testing?') });
    fatherGeneticTests.add(new Field('genetic_testing', YES_NO_BUTTONS, { unknownAccepted: true, id: 'paternal-grandmother-gt', root: 'paternal_grandfather' }));
    const fatherGeneticTestsListSection = new Section('paternal-grandmother-genetic-tests-list', {
        condition: (fields) => YES === getPropsOrDefault(fields, 'paternal_grandfather.genetic_testing', UNKNOWN)
    });
    fatherGeneticTestsListSection.add(new Field('genetic_testing_list', AUTOCOMPLETE_CHECKBOX, {
        id: 'paternal-grandfather-genetic-testing-list',
        title: _('For which genes?'),
        unique: true,
        multipleChoice: true,
        variants: GENETIC_TESTS_LIST,
        defaultVariants: [ 1, 2, 3, 4, 5, 6 ],
        placeholder: _('Other, please describe'),
        root: 'paternal_grandfather'
    }));

    FATHER_PARENTS.include([
        grandMother, motherDobOrDied, motherGeneticTests, motherGeneticTestsListSection,
        grandFather, fatherDobOrDied, fatherGeneticTests, fatherGeneticTestsListSection
    ]);
}
/*--------------------------------------------------------------------------------------------------------------------*/

/*--------------------------------------------------COMMENTS----------------------------------------------------------*/
const COMMENTS = new Page('comments', { title: _(`Comments`) });
{
    const userComments = new Row('comments', { disableNumber: true, title: _(`Feel free to provide any additional information about your family health history that wasn't already captured, or any comments/questions for your healthcare provider in the field below.`) });
    userComments.add(new Field('comments', USER_COMMENTS, {
        placeholder: `${_('Example:')} ${_('Maternal aunt with ovarian cancer at 60.')}`
    }));

    COMMENTS.include([userComments]);
}
/*--------------------------------------------------------------------------------------------------------------------*/

/*------------------------------------------------FINAL STEP----------------------------------------------------------*/
const FINAL_STEP = new Page('final-step', { title: _(`Final Step`) });
{
    const finalStep = new Row('final-step', { disableNumber: true, title: _(`At this point, you have completed your family health history. You can scroll back to review your answers, or click "Complete Questionnaire" to save your answers and send them to your healthcare provider.`) });
    FINAL_STEP.include([finalStep]);
}
/*--------------------------------------------------------------------------------------------------------------------*/

/*------------------------------------------------FINAL PAGE----------------------------------------------------------*/
const FINISH_PAGE = new FinishPage('finish-page', { title: _('Finish') });
/*--------------------------------------------------------------------------------------------------------------------*/

/*-------------------------------------------CONFIGURATION EXPORT-----------------------------------------------------*/
const cancerQuestionnaire = new Configuration('Cancer questionnaire');
export default cancerQuestionnaire.include([
    PERSONAL_INFORMATION,
    WELCOME_PAGE,
    GROUP_HEALTH_INFORMATION,
    REPRODUCTIVE_HISTORY,
    CANCER_HISTORY,
    GENETIC_TESTING,
    SURGICAL_HISTORY,
    FAMILY_TREE,
    MOTHER,
    FATHER,
    CHILDREN,
    SIBLINGS,
    NEPHEWS,
    MOTHERS_SIBLINGS,
    FATHERS_SIBLINGS,
    COUSINS,
    MOTHERS_PARENTS,
    FATHER_PARENTS,
    COMMENTS,
    FINAL_STEP,
    FINISH_PAGE
]).validate();

